import Markdown from '@wandb/weave/common/components/Markdown';
import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {fuzzyMatchWithMapping} from '@wandb/weave/common/util/fuzzyMatch';
import {Link} from '@wandb/weave/common/util/links';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Icon} from '@wandb/weave/components/Icon';
import {isValidEmail} from '@wandb/weave/core';
import _ from 'lodash';
// eslint-disable-next-line wandb/no-deprecated-imports
import qs from 'query-string';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Card,
  Container,
  DropdownItemProps,
  Header,
  Input,
  StrictInputProps,
} from 'semantic-ui-react';

import ImageIconHeader from '../components/ImageIconHeader';
import {useHideNavOnPage} from '../components/NavContextProvider';
import {PageWithSidebar} from '../components/PageWithSidebar';
import ProfileMenu from '../components/Search/SearchNav/ProfileMenu';
import {
  useCreateNewsletterSubscriptionMutation,
  useNewsletterSubscriptionQuery,
  useStarViewMutation,
  useUnstarViewMutation,
} from '../generated/graphql';
import {GALLERY_PATH_SEGMENT} from '../routes/paths';
import {auth} from '../setup';
import {usePrevious} from '../state/hooks';
import {PanelBankContextProvider} from '../state/panelbank/context';
import {useViewer} from '../state/viewer/hooks';
import docURLs from '../util/doc_urls';
import {propagateErrorsContext} from '../util/errors';
import {
  BizzaboEvent,
  DEFAULT_LANGUAGE,
  deriveTagPage,
  DISCUSSION_CATEGORY_ID,
  EMPTY_GALLERY_SPEC,
  FC_NAV_LINKS,
  FCNavLink,
  FCPage,
  FeaturedReportCategory,
  formatGalleryTag,
  GallerySpec,
  getGalleryTagQS,
  getLabelForFCPage,
  getSetActiveRepoLink,
  getSetActiveTagLink,
  getSetActiveTopicLink,
  getSetPageLink,
  isFCPage,
  isLanguage,
  Language,
  LANGUAGES,
  MAIN_PAGE,
  ReportIDWithTagIDs,
  ReportMetadata,
  RepoWithUsage,
  TAG_PAGES,
  TagV2,
  useBizzaboEvents,
  useGallerySpec,
  useProjectUsages,
  useReportMetadata,
  useReportMetadataWithCache,
  useReportTags,
  useTagReportData,
} from '../util/gallery';
import {useDebounceState, useScrollIntoView} from '../util/hooks';
import {imgSrcWithDefault} from '../util/images';
import {parseReportParams} from '../util/report';
import * as URLs from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import * as S from './FC.styles';
import {DropdownTrigger, PostWrapper} from './GalleryPage';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ReportPageContainer} from './ReportPage/ReportPageContainer';
import {ReportPageContextProvider} from './ReportPage/ReportPageContext';
import {Weave1PanelContextProvider} from './ReportPage/Weave1PanelContext';

type RouteParams = NormalParams | ReportPageParams | RepoParams | TopicParams;

type NormalParams = {
  page?: string;
  tag?: string;
};

type ReportPageParams = {
  entityName: string;
  projectName: string;
  reportNameAndID: string;
};

type RepoParams = {
  repoName: string;
};

type TopicParams = {
  topic: string;
};

function isReportPageParams(p: RouteParams): p is ReportPageParams {
  return `reportNameAndID` in p;
}

function isRepoParams(p: RouteParams): p is RepoParams {
  return `repoName` in p;
}

function isTopicParams(p: RouteParams): p is TopicParams {
  return `topic` in p;
}

const useLanguageOptions = () => {
  const history = useHistory();

  return LANGUAGES.map(o => ({
    text: o,
    onClick: () => {
      history.push({
        search: o === DEFAULT_LANGUAGE ? '' : `?language=${o}`,
      });
    },
  }));
};

type ProductsDropdownLink = {
  url: string;
  name: string;
  description: string;
  imageURL: string;
};

const PRODUCTS_DROPDOWN_LINKS: ProductsDropdownLink[] = [
  {
    url: URLs.experimentTracking(),
    name: `Experiments`,
    description: `Lightweight experiment tracking`,
    imageURL: `/fc/nav-icon-experiments.svg`,
  },
  {
    url: URLs.reports(),
    name: `Reports`,
    description: `Collaborative dashboards`,
    imageURL: `/fc/nav-icon-reports.svg`,
  },
  {
    url: URLs.artifacts(),
    name: `Artifacts`,
    description: `Dataset and model versioning`,
    imageURL: `/fc/nav-icon-artifacts.svg`,
  },
  {
    url: URLs.tables(),
    name: `Tables`,
    description: `Interactive data visualization`,
    imageURL: `/fc/nav-icon-tables.svg`,
  },
  {
    url: URLs.sweeps(),
    name: `Sweeps`,
    description: `Hyperparameter optimization`,
    imageURL: `/fc/nav-icon-sweeps.svg`,
  },
  {
    url: URLs.models(),
    name: `Models`,
    description: `Centralized model registry`,
    imageURL: `/fc/nav-icon-models.png`,
  },
  {
    url: URLs.launch(),
    name: `Launch`,
    description: `Automated ML workflows`,
    imageURL: `/fc/nav-icon-launch.png`,
  },
];

const PRODUCTS_DROPDOWN_ITEMS: DropdownItemProps[] =
  PRODUCTS_DROPDOWN_LINKS.map(({url, name, description, imageURL}) => ({
    content: (
      <S.TopNavDropdownItem to={url}>
        <S.TopNavDropdownItemImage src={imageURL} />
        <S.TopNavDropdownItemText>
          <b>{name}</b>
          <br />
          {description}
        </S.TopNavDropdownItemText>
      </S.TopNavDropdownItem>
    ),
  }));

type ResourcesDropdownLink = {
  name: string;
} & ({url: string} | {page: FCPage});

const RESOURCES_DROPDOWN_LINKS: ResourcesDropdownLink[] = [
  {
    url: URLs.fullyConnected(),
    name: `Blog`,
  },
  {
    url: URLs.articles(),
    name: `Articles`,
  },
  {
    url: URLs.podcast(),
    name: `Podcast`,
  },
  {
    page: `events`,
    name: `Webinars`,
  },
  {
    url: URLs.courses(),
    name: `Courses`,
  },
  {
    url: URLs.community(),
    name: `Community`,
  },
  {
    url: URLs.caseStudies(),
    name: `Case Studies`,
  },
  {
    url: URLs.tutorials(),
    name: `Tutorials`,
  },
  {
    url: URLs.benchmarks(),
    name: `Benchmarks`,
  },
  {
    url: URLs.company(),
    name: `Company`,
  },
  {
    url: URLs.careers(),
    name: `Careers`,
  },
];

const RESOURCES_DROPDOWN_ITEMS: DropdownItemProps[] =
  RESOURCES_DROPDOWN_LINKS.map(l => ({
    content: (
      <S.TopNavDropdownItem to={`url` in l ? l.url : getSetPageLink(l.page)}>
        {l.name}
      </S.TopNavDropdownItem>
    ),
  }));

type ReportParams = {
  id: string;
  entityName: string;
  projectName?: string;
  displayName: string;
};

type FCContextState = Omit<GallerySpec, 'categories' | 'tags' | 'repos'> &
  Omit<FCReportsResult, 'updateReportMetadata'> &
  StateFromURL & {
    loading: boolean;
    loadingEvents: boolean;
    loadingNewsletterSubscription: boolean;
    currentPage: number;
    minPage: number;
    maxPage: number;
    activeReportParams: ReportParams | null;
    searchQuery: string;
    searchQueryDebounced: string;
    homeCategoryName: string;
    subscribeModalOpen: boolean;
    events: BizzaboEvent[];
    reposWithGitHubMetadata: RepoWithGitHubMetadata[];
    subscribedToNewsletter: boolean;
    reportIDSetByTagName: Map<string, Set<string>>;
    getSetCurrentPageLink: (
      p: number
    ) => Partial<ReturnType<typeof useLocation>>;
    reportBelongsToTag: (reportID: string, tagName: string) => boolean;
  };

const FCContext = React.createContext<FCContextState>({
  ...EMPTY_GALLERY_SPEC,
  loading: false,
  loadingReports: false,
  loadingEvents: false,
  loadingNewsletterSubscription: false,
  page: MAIN_PAGE,
  reports: [],
  newReports: [],
  popularReports: [],
  currentPage: 1,
  minPage: 1,
  maxPage: 1,
  activeTag: ``,
  activeReportID: null,
  activeReportParams: null,
  activeRepo: null,
  activeTopic: null,
  language: DEFAULT_LANGUAGE,
  searchQuery: ``,
  searchQueryDebounced: ``,
  homeCategoryName: ``,
  subscribeModalOpen: false,
  events: [],
  reposWithGitHubMetadata: [],
  subscribedToNewsletter: false,
  reportIDSetByTagName: new Map(),
  getSetCurrentPageLink: () => ({}),
  reportBelongsToTag: () => false,
});

type FCContextUpdaters = {
  setPage: (page: FCPage) => void;
  setActiveReportID: (id: string) => void;
  setActiveRepo: (repo: string) => void;
  setSearchQuery: (query: string) => void;
  setSearchQueryLiveAndDebounced: (query: string) => void;
  setSubscribeModalOpen: (open: boolean) => void;
  updateReportMetadata: (
    reportID: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
};

const FCUpdaterContext = React.createContext<FCContextUpdaters>({
  setPage: () => {},
  setActiveReportID: () => {},
  setActiveRepo: () => {},
  setSearchQuery: () => {},
  setSearchQueryLiveAndDebounced: () => {},
  setSubscribeModalOpen: () => {},
  updateReportMetadata: () => {},
});

const FCContextProvider: React.FC = React.memo(({children}) => {
  const history = useHistory();
  const viewer = useViewer();
  const {params} = useRouteMatch<RouteParams>();

  const {
    loading: loadingNewsletterSubscription,
    data: newsletterSubscriptionData,
  } = useNewsletterSubscriptionQuery({
    variables: {
      userName: viewer?.username ?? '',
    },
    skip: !viewer?.username,
  });
  const subscribedToNewsletter = useMemo(
    () => newsletterSubscriptionData?.user?.newsletterSubscription != null,
    [newsletterSubscriptionData]
  );

  const {loading, gallerySpec} = useGallerySpec();
  const {tagsV2, reportIDsWithTagV2IDs, repos, eventMetadatas} = gallerySpec;

  const {projectUsages} = useProjectUsages();
  const projectUsageByProjectName = useMemo(
    () => new Map(projectUsages.map(u => [u.projectName, u])),
    [projectUsages]
  );
  const reposWithUsage: RepoWithUsage[] = useMemo(
    () =>
      repos
        .filter(r => !r.pending)
        .map(r => {
          const projectUsage = projectUsageByProjectName.get(r.name);
          return {...r, userCounts: projectUsage?.userCounts ?? []};
        }),
    [repos, projectUsageByProjectName]
  );
  const reposWithGitHubMetadata = useGitHubMetadata(reposWithUsage);

  const {loading: loadingEvents, events} = useBizzaboEvents(eventMetadatas);

  const homeCategoryName = useMemo(
    () => tagsV2.find(t => t.linkForm === '')?.name ?? MAIN_PAGE,
    [tagsV2]
  );

  const {reportIDSetByTagName, reportBelongsToTag} = useTagReportData();

  // Read state from URL
  const {
    page,
    activeTag,
    activeReportID,
    activeRepo,
    activeTopic,
    language,
    currentPage,
  } = useDeriveStateFromURL({
    params,
    reportIDsWithTagV2IDs,
    tags: tagsV2,
    homeCategoryName,
  });

  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

  const [
    searchQuery,
    searchQueryDebounced,
    setSearchQuery,
    setSearchQueryLiveAndDebounced,
  ] = useDebounceState('', 300);

  const {reportParams: activeReportParams} =
    useActiveReportParams(activeReportID);

  const {
    loadingReports,
    newReports,
    popularReports,
    reports: allReports,
    updateReportMetadata,
  } = useFCReports({
    reportIDsWithTagV2IDs,
    tagsV2,
    activeTag,
    language,
    searchQueryDebounced,
    reportBelongsToTag,
  });

  const getSetCurrentPageLink = useCallback(
    (p: number) => {
      const searchObj = qs.parse(history.location.search);

      if (p === 1) {
        delete searchObj.page;
      } else {
        searchObj.page = String(p);
      }

      return {
        pathname: history.location.pathname,
        search: qs.stringify(searchObj),
      };
    },
    [history]
  );
  const setCurrentPage = useCallback(
    (p: number) => {
      history.push(getSetCurrentPageLink(p));
    },
    [getSetCurrentPageLink, history]
  );

  const setPage = useCallback(
    (newPage: FCPage) => {
      if (newPage === MAIN_PAGE) {
        history.push(`/${GALLERY_PATH_SEGMENT}`);
        return;
      }
      const pathSegments = [GALLERY_PATH_SEGMENT, newPage];
      history.push(`/${pathSegments.join(`/`)}`);
    },
    [history]
  );
  const setActiveReportID = useCallback(
    (id: string) => {
      const pathSegments = [GALLERY_PATH_SEGMENT, `blog`, `tag`, id];
      history.push(`/${pathSegments.join(`/`)}`);
    },
    [history]
  );
  const setActiveRepo = useCallback(
    (name: string) => {
      const pathSegments = [GALLERY_PATH_SEGMENT, `projects`, name];
      history.push(`/${pathSegments.join(`/`)}`);
    },
    [history]
  );

  const {
    currentItems: reports,
    minPage,
    maxPage,
  } = usePagination({
    loadingItems: loadingReports,
    allItems: allReports,
    itemsPerPage: 20,
    currentPage,
    setCurrentPage,
  });

  useEffect(() => {
    window.analytics?.page(`FC`, {
      fcPage:
        activeReportID != null
          ? `report`
          : activeRepo != null
            ? `project`
            : page,
      reportID: activeReportID,
      project: activeRepo,
    });
  }, [page, activeReportID, activeRepo]);

  const state = useMemo<FCContextState>(
    () => ({
      loading,
      loadingReports,
      loadingEvents,
      loadingNewsletterSubscription,
      ...gallerySpec,
      reports,
      newReports,
      popularReports,
      currentPage,
      minPage,
      maxPage,
      homeCategoryName,
      page,
      activeTag,
      activeReportID,
      activeReportParams,
      activeRepo,
      activeTopic,
      language,
      events,
      reposWithGitHubMetadata,
      searchQuery,
      searchQueryDebounced,
      subscribeModalOpen,
      subscribedToNewsletter,
      reportIDSetByTagName,
      getSetCurrentPageLink,
      reportBelongsToTag,
    }),
    [
      loading,
      loadingReports,
      loadingEvents,
      loadingNewsletterSubscription,
      gallerySpec,
      reports,
      newReports,
      popularReports,
      currentPage,
      minPage,
      maxPage,
      homeCategoryName,
      page,
      activeTag,
      activeReportID,
      activeReportParams,
      activeRepo,
      activeTopic,
      language,
      events,
      reposWithGitHubMetadata,
      searchQuery,
      searchQueryDebounced,
      subscribeModalOpen,
      subscribedToNewsletter,
      reportIDSetByTagName,
      getSetCurrentPageLink,
      reportBelongsToTag,
    ]
  );

  const updaters = useMemo<FCContextUpdaters>(
    () => ({
      setPage,
      setActiveReportID,
      setActiveRepo,
      setSearchQuery,
      setSearchQueryLiveAndDebounced,
      setSubscribeModalOpen,
      updateReportMetadata,
    }),
    [
      setPage,
      setActiveReportID,
      setActiveRepo,
      setSearchQuery,
      setSearchQueryLiveAndDebounced,
      updateReportMetadata,
    ]
  );

  return (
    <FCContext.Provider value={state}>
      <FCUpdaterContext.Provider value={updaters}>
        {children}
      </FCUpdaterContext.Provider>
    </FCContext.Provider>
  );
});

function useFCContext(): FCContextState {
  return useContext(FCContext);
}
function useFCContextUpdaters(): FCContextUpdaters {
  return useContext(FCUpdaterContext);
}

const FCWithContext: React.FC = React.memo(() => {
  useHideNavOnPage();
  return (
    <FCContextProvider>
      <FC />
    </FCContextProvider>
  );
});

export default FCWithContext;

type FCProps = {};

const FC: React.FC<FCProps> = React.memo(() => {
  const {
    page,
    activeTag,
    activeReportID,
    activeReportParams,
    activeRepo,
    activeTopic,
  } = useFCContext();
  const {path} = useRouteMatch();

  const {ref: pageRef, trigger: scrollToTopOfPage} =
    useScrollIntoView<HTMLDivElement>(0);
  // useEffectExceptFirstRender(() => {
  //   scrollToTopOfPage();
  // }, [page, activeTag, activeReportID, activeRepo, activeTopic]);

  return (
    <>
      <S.Page data-test="fc-page">
        <Desktop>
          <FCTopNav />
        </Desktop>
        <Mobile>{activeReportID == null && <FCTopNav />}</Mobile>
        {activeReportID == null && <FCIntro />}

        <S.FCPageContainer ref={pageRef} viewingReport={activeReportID != null}>
          <FCBreadcrumbs />
          <S.FCContent
            wider={activeRepo != null}
            reportMode={activeReportID != null}>
            {activeReportID != null ? (
              activeReportParams != null ? (
                <ReportPageContextProvider
                  path={path}
                  draftId={null}
                  entityName={activeReportParams.entityName}
                  projectName={activeReportParams.projectName}
                  reportId={activeReportParams.id}
                  reportName={activeReportParams.displayName}
                  isWBFeaturedReport>
                  <PanelBankContextProvider>
                    <Weave1PanelContextProvider>
                      <ReportPageContainer />
                    </Weave1PanelContextProvider>
                  </PanelBankContextProvider>
                </ReportPageContextProvider>
              ) : (
                <LoadingContent />
              )
            ) : activeRepo != null ? (
              <RepoPage />
            ) : ['blog', ...TAG_PAGES].includes(page) ? (
              <BlogPage />
            ) : page === `events` ? (
              <EventsPage />
            ) : page === `projects` ? (
              <ReposPage />
            ) : page === `topics` ? (
              <TopicPage />
            ) : null}
          </S.FCContent>
        </S.FCPageContainer>

        <FCCTA />
        <FCFooter />
      </S.Page>

      <SubscribeModal />
    </>
  );
});

type FCTopNavProps = {};

const FCTopNav: React.FC<FCTopNavProps> = React.memo(() => {
  const viewer = useViewer();
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

  return (
    <S.TopNav>
      <S.TopNavContent>
        <S.TopNavLogo to={getSetPageLink(MAIN_PAGE)}>
          <S.TopNavLogoImage src={`/logo-transparent.png`} />
          Weights & Biases
        </S.TopNavLogo>
        <Desktop>
          <S.TopNavDropdown
            on="hover"
            MenuComponent={S.TopNavDropdownMenu}
            options={PRODUCTS_DROPDOWN_ITEMS}
            offset={`0px, -16px`}
            trigger={
              <div>
                <S.TopNavDropdownTrigger>
                  Products
                  <S.TopNavDropdownIcon name="chevron-down" />
                </S.TopNavDropdownTrigger>
              </div>
            }
          />
          <S.TopNavDropdown
            on="hover"
            MenuComponent={S.TopNavDropdownMenu}
            options={RESOURCES_DROPDOWN_ITEMS}
            offset={`0px, -16px`}
            trigger={
              <div>
                <S.TopNavDropdownTrigger>
                  Resources
                  <S.TopNavDropdownIcon name="chevron-down" />
                </S.TopNavDropdownTrigger>
              </div>
            }
          />
          <S.TopNavLink to={docURLs.root}>Docs</S.TopNavLink>
          <S.TopNavLink to={URLs.pricing()}>Pricing</S.TopNavLink>
          <S.TopNavLink to={URLs.enterprise()}>Enterprise</S.TopNavLink>
          {viewer ? (
            <S.ProfileDropdownMenuContainer className={`search-nav`}>
              <ProfileMenu isFullyConnected={true} />
            </S.ProfileDropdownMenuContainer>
          ) : (
            <S.LoginButtons>
              <S.LoginLink to={URLs.login()}>
                <S.LoginLinkIcon name="lock-closed" />
                Log in
              </S.LoginLink>
              <S.LoginLink to={URLs.login({signup: true})}>Sign up</S.LoginLink>
            </S.LoginButtons>
          )}
        </Desktop>
        <Mobile>
          <S.MobileNavMenuTrigger
            onClick={() => setMobileNavMenuOpen(prev => !prev)}>
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenu
              active={mobileNavMenuOpen}
              onClick={e => e.stopPropagation()}>
              {viewer == null && (
                <>
                  <S.MobileNavLink to={URLs.login()}>Log in</S.MobileNavLink>
                  <S.MobileNavLink to={URLs.login({signup: true})}>
                    Sign up
                  </S.MobileNavLink>
                </>
              )}
              <S.MobileNavDropdownTrigger>
                Products{' '}
                <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
              </S.MobileNavDropdownTrigger>
              <S.MobileNavDropdown>
                {PRODUCTS_DROPDOWN_LINKS.map(
                  ({url, name, description, imageURL}, i) => (
                    <S.TopNavDropdownItem withPadding key={i} to={url}>
                      <S.TopNavDropdownItemImage src={imageURL} />
                      <S.TopNavDropdownItemText>
                        <b>{name}</b>
                        <br />
                        {description}
                      </S.TopNavDropdownItemText>
                    </S.TopNavDropdownItem>
                  )
                )}
              </S.MobileNavDropdown>
              <S.MobileNavDropdownTrigger>
                Resources{' '}
                <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
              </S.MobileNavDropdownTrigger>
              <S.MobileNavDropdown>
                {RESOURCES_DROPDOWN_LINKS.map((l, i) => (
                  <S.MobileNavDropdownLink
                    key={i}
                    to={`url` in l ? l.url : getSetPageLink(l.page)}>
                    {l.name}
                  </S.MobileNavDropdownLink>
                ))}
              </S.MobileNavDropdown>
              <S.MobileNavLink to={docURLs.root}>Docs</S.MobileNavLink>
              <S.MobileNavLink to={URLs.pricing()}>Pricing</S.MobileNavLink>
              <S.MobileNavLink to={URLs.enterprise()}>
                Enterprise
              </S.MobileNavLink>
            </S.MobileNavMenu>
          </S.MobileNavMenuTrigger>
        </Mobile>
      </S.TopNavContent>
    </S.TopNav>
  );
});

type FCIntroProps = {};

const FCIntro: React.FC<FCIntroProps> = React.memo(() => {
  const {
    page,
    activeRepo,
    subscribedToNewsletter,
    loadingNewsletterSubscription,
  } = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();

  const showSubscribeButton =
    !subscribedToNewsletter && !loadingNewsletterSubscription;

  return (
    <S.Intro>
      <S.IntroContent>
        <div className="fully-connected-banner-may-2025">
          <a
            href="https://wandb.ai/wandb_fc/product-announcements-fc/reports/From-Online-Evaluations-to-Inference-here-s-what-we-announced-at-Fully-Connected-2025--VmlldzoxMzIzODM2Mw"
            className="fc25-banner">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="fcbanner-icon">
              <path
                d="M13.5 2.75C13.5 2.33579 13.1642 2 12.75 2C12.3358 2 12 2.33579 12 2.75V4.75C12 5.16421 12.3358 5.5 12.75 5.5C13.1642 5.5 13.5 5.16421 13.5 4.75V2.75Z"
                fill="#2B3038"></path>
              <path
                d="M19.4403 4.55967C19.7332 4.85256 19.7332 5.32743 19.4403 5.62033L17.7303 7.33033C17.4374 7.62323 16.9625 7.62323 16.6697 7.33033C16.3768 7.03744 16.3768 6.56257 16.6697 6.26967L18.3797 4.55967C18.6726 4.26677 19.1474 4.26677 19.4403 4.55967Z"
                fill="#2B3038"></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.6503 6.11967C10.3574 5.82677 9.88253 5.82677 9.58964 6.11966C9.29674 6.41256 9.29674 6.88743 9.58963 7.18032L9.82253 7.41322L4.29673 14.2485C4.29152 14.2549 4.28642 14.2615 4.28146 14.268C3.83084 14.7209 3.42344 15.1515 3.10107 15.5701C2.76078 16.012 2.48003 16.4844 2.35421 17.0055C2.07953 18.143 2.60792 19.1586 3.64963 20.2003C4.6911 21.2418 5.70599 21.7719 6.84349 21.4974C7.36451 21.3717 7.83689 21.0908 8.27877 20.7503C8.55023 20.5411 8.82688 20.2961 9.11151 20.0266L11.2984 22.2135C11.5913 22.5064 12.0662 22.5064 12.3591 22.2135C12.652 21.9206 12.652 21.4457 12.3591 21.1528L10.2322 19.026L16.4309 14.0216L16.6596 14.2503C16.9525 14.5432 17.4274 14.5432 17.7203 14.2503C18.0132 13.9574 18.0132 13.4826 17.7203 13.1897L17.088 12.5574C17.0673 12.53 17.0445 12.5038 17.0198 12.4792L15.3937 10.8559C15.8224 10.1749 15.7178 9.27721 15.1403 8.69966C14.557 8.11632 13.6649 8.02021 12.9828 8.44927L11.3598 6.8292L11.3583 6.82768L10.6503 6.11967ZM15.3642 12.9549L9.17903 17.9484L5.89189 14.6613L10.8891 8.47982L15.3642 12.9549ZM4.88455 15.7996C4.65142 16.0471 4.45318 16.2728 4.2895 16.4853C4.01165 16.8461 3.86846 17.125 3.8123 17.3576C3.72302 17.7273 3.79846 18.2278 4.71029 19.1397C5.62342 20.0528 6.12305 20.1282 6.49165 20.0393C6.72369 19.9833 7.00226 19.8403 7.36319 19.5621C7.57608 19.3981 7.80227 19.1993 8.0504 18.9655L4.88455 15.7996Z"
                fill="#2B3038"></path>
              <path
                d="M19.25 10.5C18.8358 10.5 18.5 10.8358 18.5 11.25C18.5 11.6642 18.8358 12 19.25 12H21.25C21.6642 12 22 11.6642 22 11.25C22 10.8358 21.6642 10.5 21.25 10.5H19.25Z"
                fill="#2B3038"></path>
            </svg>
            <span className="fc-banner-message">
              Announcing new AI cloud software products and capabilities from
              CoreWeave and Weights & Biases
            </span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="24"
              viewBox="0 0 21 24"
              fill="none"
              className="arrow-icon">
              <path
                d="M17.0458 13C17.0458 13.0919 17.0262 13.1792 16.991 13.258L16.8878 13.4181C16.8791 13.428 16.87 13.4377 16.8606 13.4471L10.7584 19.5493C10.5114 19.7963 10.1111 19.7963 9.86416 19.5493C9.61723 19.3024 9.61723 18.9021 9.86416 18.6551L14.887 13.6323H3.99948C3.65028 13.6323 3.36719 13.3492 3.36719 13C3.36719 12.6508 3.65028 12.3677 3.99948 12.3677H14.887L9.86416 7.34484C9.61724 7.09792 9.61724 6.69757 9.86416 6.45065C10.1111 6.20372 10.5114 6.20372 10.7584 6.45064L16.8606 12.5529L16.862 12.5542C16.9202 12.6128 16.9646 12.6799 16.9951 12.7515C17.0278 12.8278 17.0458 12.9118 17.0458 13Z"
                fill="#2B3038"></path>
            </svg>
          </a>
        </div>
        <style>
          {`
            .fully-connected-banner-may-2025 {
              display: flex;
              flex-direction: row;
              justify-content: center;
              align-items: center;
              width: 100%;
              padding: 20px;
            }

            .fc25-banner {
              height: 36px;
              border-radius: 36px;
              display: flex;
              font-size: 16px;
              width: auto;
              font-weight: 600;
              flex-direction: row;
              align-items: center;
              padding: 6px 10px;
              gap: 65px;
              background: linear-gradient(270deg, #FFCC33 0%, #FFAD33 100%);
              flex-grow: 0;
              flex-shrink: 0;
              margin: 0;
              transition: all 250ms ease-out;
              flex-wrap: nowrap;
              text-decoration: none;
              background-size: 200% 200%;
              color: #000;
            }

            .fc25-banner .fcbanner-icon {
              width: 24px;
              height: 24px;
              filter: brightness(0) saturate(100%);
              flex-shrink: 0;
            }

            .fc25-banner .fc-banner-message {
              font-size: 16px;
              font-weight: 600;
              line-height: 1.4;
              flex: 1;
              text-align: center;
            }

            .fc25-banner .arrow-icon {
              width: 20px;
              height: 20px;
              filter: brightness(0) saturate(100%);
              flex-shrink: 0;
              transition: transform 0.2s ease;
            }

            .fc25-banner:hover .arrow-icon {
              transform: translateX(4px);
            }

            @media (max-width: 768px) {
              .fc25-banner {
                gap: 10px;
                padding: 6px 8px;
                max-width: 100%;
                height: auto;
              }

              .fc25-banner .fc-banner-message {
                font-size: 12px;
                text-align: left;
              }

              .fc25-banner .fcbanner-icon {
                width: 20px;
                height: 20px;
              }

              .fc25-banner .arrow-icon {
                width: 18px;
                height: 18px;
              }
            }
          `}
        </style>
        {showSubscribeButton && (
          <S.IntroButtons>
            {showSubscribeButton && (
              <S.IntroButtonImg
                src={`/fc/button-subscribe.png`}
                onClick={() => setSubscribeModalOpen(true)}
              />
            )}
          </S.IntroButtons>
        )}
      </S.IntroContent>
    </S.Intro>
  );
});

type FCNavProps = {};

const FCNav: React.FC<FCNavProps> = React.memo(() => {
  const {page} = useFCContext();

  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

  return (
    <>
      <Desktop>
        <S.FCNav>
          <S.FCNavContent>
            {FC_NAV_LINKS.map((l, i) => (
              <S.FCNavLink
                key={i}
                to={`page` in l ? getSetPageLink(l.page) : l.url}
                onClick={() => {
                  trackClickPage(l, `FC nav`);
                }}
                active={`page` in l && page === l.page}>
                {l.label}
              </S.FCNavLink>
            ))}
          </S.FCNavContent>
        </S.FCNav>
      </Desktop>
      <Mobile>
        <S.MobileNav>
          <S.MobileNavText>Weights & Biases</S.MobileNavText>
          <S.MobileNavMenuTrigger
            onClick={() => setMobileNavMenuOpen(prev => !prev)}>
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenuTriggerBar />
            <S.MobileNavMenu
              active={mobileNavMenuOpen}
              onClick={e => e.stopPropagation()}>
              {FC_NAV_LINKS.map((l, i) => (
                <S.MobileNavLink
                  key={i}
                  to={`page` in l ? getSetPageLink(l.page) : l.url}
                  onClick={() => {
                    trackClickPage(l, `FC mobile pages nav menu`);
                  }}>
                  {l.label}
                </S.MobileNavLink>
              ))}
            </S.MobileNavMenu>
          </S.MobileNavMenuTrigger>
        </S.MobileNav>
      </Mobile>
    </>
  );

  function trackClickPage(l: FCNavLink, location: string): void {
    window.analytics?.track(`FC Page Clicked`, {
      location,
      name: l.label,
    });
  }
});

type FCCTAProps = {};

const FCCTA: React.FC<FCCTAProps> = React.memo(() => {
  const viewer = useViewer();
  return (
    <S.CTA>
      <S.CTAContent>
        <S.CTAText>
          Iterate on AI agents
          <ResponsiveSpace />
          and models faster.
          <ResponsiveSpace />
          <S.GoldText>Try Weights & Biases today.</S.GoldText>
        </S.CTAText>
        <S.CTAButtons>
          {viewer == null && (
            <S.CTAButtonGold data-test="signup" to={URLs.login({signup: true})}>
              Sign up
            </S.CTAButtonGold>
          )}
          <S.CTAButtonWhite to={docURLs.gettingStarted}>
            Try W&B now
          </S.CTAButtonWhite>
        </S.CTAButtons>
      </S.CTAContent>
    </S.CTA>
  );
});

// type FCFooterSocialLink = {
//   url: string;
//   icon: string;
// };

// const FOOTER_SOCIAL_LINKS: FCFooterSocialLink[] = [
//   {url: URLs.facebook(), icon: ``},
//   {url: URLs.instagram(), icon: ``},
//   {url: URLs.twitter(), icon: ``},
//   {url: URLs.youtube(), icon: ``},
// ];

type FCFooterNavSection = {
  header: string;
  links: FCFooterNavLink[];
};

type FCFooterNavLink = {
  url: string;
  text: string;
  mobileOnly?: true;
};

const FOOTER_NAV_SECTIONS: FCFooterNavSection[] = [
  {
    header: `Products`,
    links: [
      {url: URLs.experimentTracking(), text: `Dashboard`},
      {url: URLs.sweeps(), text: `Sweeps`},
      {url: URLs.artifacts(), text: `Artifacts`},
      {url: URLs.reports(), text: `Reports`},
      {url: URLs.tables(), text: `Tables`},
    ],
  },
  {header: `Quickstart`, links: [{url: docURLs.root, text: `Documentation`}]},
  {
    header: `Resources`,
    links: [
      {url: URLs.courses(), text: `Courses`},
      {url: URLs.community(), text: `Forum`},
      {url: URLs.tutorials(), text: `Tutorials`},
      {url: URLs.benchmarks(), text: `Benchmarks`},
    ],
  },
  {
    header: `W&B`,
    links: [
      {url: URLs.company(), text: `About Us`},
      {url: URLs.authors(), text: `Authors`},
      {url: URLs.contact(), text: `Contact`},
      {url: URLs.termsOfService(), text: `Terms of Service`, mobileOnly: true},
      {url: URLs.privacyPolicy(), text: `Privacy Policy`, mobileOnly: true},
    ],
  },
];

type FCFooterProps = {};

const FCFooter: React.FC<FCFooterProps> = React.memo(() => {
  const {subscribedToNewsletter, loadingNewsletterSubscription} =
    useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  return (
    <S.Footer>
      <S.FooterContent>
        <S.FooterMain>
          <S.FooterLeftColumn>
            <S.FooterLogo>
              <S.FooterLogoImage src={`/logo-transparent.png`} />
              Weights & Biases
            </S.FooterLogo>
            {/* <S.FooterSocials>
              <S.FooterSocialText>Stay Connected</S.FooterSocialText>
              <S.FooterSocialLinks>
                {FOOTER_SOCIAL_LINKS.map(({url, icon}, i) => (
                  <S.FooterSocialLink key={i} to={url}></S.FooterSocialLink>
                ))}
              </S.FooterSocialLinks>
            </S.FooterSocials> */}
            {!subscribedToNewsletter && !loadingNewsletterSubscription && (
              <S.FooterSubscribe>
                Get weekly updates with the latest ML news.
                <S.FooterSubscribeButton
                  onClick={() => setSubscribeModalOpen(true)}>
                  Subscribe
                </S.FooterSubscribeButton>
              </S.FooterSubscribe>
            )}
          </S.FooterLeftColumn>
          <S.FooterNav>
            {FOOTER_NAV_SECTIONS.map(({header, links}, i) => (
              <S.FooterNavColumn key={i}>
                <S.FooterNavColumnHeader>{header}</S.FooterNavColumnHeader>
                <S.FooterNavColumnLinks>
                  {links.map(({url, text, mobileOnly}, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <S.FooterNavLinkDivider />}
                      <S.FooterNavColumnLink
                        key={j}
                        to={url}
                        mobileOnly={mobileOnly}>
                        {text}
                      </S.FooterNavColumnLink>
                    </React.Fragment>
                  ))}
                </S.FooterNavColumnLinks>
              </S.FooterNavColumn>
            ))}
          </S.FooterNav>
        </S.FooterMain>
        <S.FooterFooter>
          <S.FooterFooterContent>
            <S.FooterFooterLeftColumn>
              Copyright &copy;{new Date().getFullYear()} Weights & Biases. All
              rights reserved.
            </S.FooterFooterLeftColumn>
            <S.FooterFooterRightColumn>
              <S.FooterFooterLink to={URLs.termsOfService()}>
                Terms of Service
              </S.FooterFooterLink>
              <S.FooterFooterLink to={URLs.privacyPolicy()}>
                Privacy Policy
              </S.FooterFooterLink>
            </S.FooterFooterRightColumn>
          </S.FooterFooterContent>
        </S.FooterFooter>
      </S.FooterContent>
    </S.Footer>
  );
});

type FCBreadcrumbSegment = {
  text: string;
  linkTo?: string;
};

type FCBreadcrumbsProps = {};

const FCBreadcrumbs: React.FC<FCBreadcrumbsProps> = React.memo(() => {
  const {page, activeReportParams, activeRepo} = useFCContext();
  const {primaryTag} = useReportTags(activeReportParams?.id ?? null);

  const segments: FCBreadcrumbSegment[] = useMemo(() => {
    if (activeReportParams != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        ...(primaryTag != null
          ? [
              {
                text: primaryTag.name,
                linkTo: getSetActiveTagLink(primaryTag.linkForm),
              },
            ]
          : []),
      ];
    }
    if (activeRepo != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        {text: activeRepo},
      ];
    }
    return [];
  }, [page, activeReportParams, activeRepo, primaryTag]);

  const width =
    activeReportParams != null
      ? 1072
      : activeRepo != null
        ? 1280
        : S.MAX_PAGE_WIDTH;

  if (segments.length === 0) {
    return null;
  }

  return (
    <S.Breadcrumbs width={width}>
      <S.BreadcrumbsLink to={URLs.fullyConnected()}>
        W&B Fully Connected
      </S.BreadcrumbsLink>
      {segments.map(({text, linkTo}, i) => (
        <React.Fragment key={i}>
          <S.BreadcrumbsSeparator>{`>`}</S.BreadcrumbsSeparator>
          {linkTo ? (
            <S.BreadcrumbsLink to={linkTo}>{text}</S.BreadcrumbsLink>
          ) : (
            <S.BreadcrumbsSpan>{text}</S.BreadcrumbsSpan>
          )}
        </React.Fragment>
      ))}
    </S.Breadcrumbs>
  );
});

const POPULAR_TOPICS = [`tasks`, `framework--integration`, `domain`];

type BlogPageProps = {};

const BlogPage: React.FC<BlogPageProps> = React.memo(() => {
  const LANGUAGE_OPTIONS = useLanguageOptions();
  const viewer = useViewer();
  const [activeFeaturedCategory, setActiveFeaturedCategory] =
    useState<FeaturedReportCategory>(`new`);
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const [mobileEventIndex, setMobileEventIndex] = useState(0);

  const {
    page,
    activeTag,
    tagsV2,
    language,
    searchQuery,
    events,
    currentPage,
    reports,
    newReports,
    popularReports,
    loadingReports,
  } = useFCContext();
  const {setSearchQuery, setSearchQueryLiveAndDebounced, updateReportMetadata} =
    useFCContextUpdaters();

  const prevPage = usePrevious(currentPage);
  const {ref: reportsHeaderRef, trigger: scrollToReportsHeader} =
    useScrollIntoView<HTMLHeadingElement>();
  useEffect(() => {
    if (prevPage != null && currentPage !== prevPage) {
      scrollToReportsHeader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const [starViewMutation] = useStarViewMutation();
  const [unstarViewMutation] = useUnstarViewMutation();
  const star = useCallback(
    async (viewID: string) => {
      if (viewer == null) {
        auth.login();
        return;
      }
      updateReportMetadata(viewID, r => (r.starred = true));
      return starViewMutation({variables: {id: viewID}});
    },
    [viewer, updateReportMetadata, starViewMutation]
  );
  const unstar = useCallback(
    async (viewID: string) => {
      if (viewer == null) {
        auth.login();
        return;
      }
      updateReportMetadata(viewID, r => (r.starred = false));
      return unstarViewMutation({variables: {id: viewID}});
    },
    [viewer, updateReportMetadata, unstarViewMutation]
  );

  const renderReport = useCallback(
    (r: ReportMetadata, hidden: boolean) => {
      const link =
        (r.project != null
          ? URLs.reportView({
              entityName: r.project.entityName,
              projectName: r.project.name,
              reportID: r.id,
              reportName: r.displayName,
            })
          : (r.tags?.some(t => t.id === DISCUSSION_CATEGORY_ID)
              ? URLs.galleryDiscussionView
              : URLs.galleryPostView)({
              entityName: r.entityName,
              reportID: r.id,
              reportName: r.displayName,
            })) + getGalleryTagQS(tagsV2, activeTag);
      return (
        <S.PostWrapperWrapper key={r.id} hidden={hidden}>
          <S.PostStar
            name={r.starred ? 'star-filled' : 'star'}
            onClick={() => (r.starred ? unstar(r.id) : star(r.id))}
          />
          <PostWrapper
            {...r}
            link={link}
            activeTag={''}
            tagIsCategory={() => false}
            showDescription={false}
            loading={false}
          />
        </S.PostWrapperWrapper>
      );
    },
    [activeTag, star, tagsV2, unstar]
  );

  const futureEvents = useMemo(() => {
    return _.sortBy(
      events.filter(e => new Date(e.startDate).valueOf() > Date.now()),
      e => new Date(e.startDate).valueOf()
    );
  }, [events]);

  const isBlogPage = page === `blog`;

  return (
    <S.Columns>
      <S.LeftColumn>
        <Mobile>
          {futureEvents.length > 0 && (
            <>
              <S.SectionHeader>Upcoming Events</S.SectionHeader>
              <S.MobileEvents>
                <S.MobileEventsLeft
                  hidden={mobileEventIndex <= 0}
                  onClick={() => setMobileEventIndex(prev => prev - 1)}
                />
                {futureEvents
                  .slice(mobileEventIndex, mobileEventIndex + 1)
                  .map(e => (
                    <S.BlogPageEvent key={e.id} to={e.url} noMargin>
                      <S.BlogPageEventTime>
                        {new Date(e.startDate).toDateString()}
                      </S.BlogPageEventTime>
                      <S.BlogPageEventName>{e.name}</S.BlogPageEventName>
                      <S.BlogPageEventImage
                        src={e.fcMetadata?.imageURL ?? `/logo-transparent.png`}
                      />
                    </S.BlogPageEvent>
                  ))}
                <S.MobileEventsRight
                  hidden={mobileEventIndex >= futureEvents.length - 1}
                  onClick={() => setMobileEventIndex(prev => prev + 1)}
                />
              </S.MobileEvents>
            </>
          )}
          {isBlogPage && <S.SectionHeader>Featured ML Posts</S.SectionHeader>}
        </Mobile>
        {newReports.length > 0 || popularReports.length > 0 ? (
          <>
            <S.SectionPicker>
              <S.SectionPickerItem
                active={activeFeaturedCategory === `new`}
                onClick={() => setActiveFeaturedCategory(`new`)}>
                <S.SectionHeader>New</S.SectionHeader>
                <S.SectionPickerIcon src={`/fc/icon-star.png`} />
              </S.SectionPickerItem>
              <S.SectionPickerItem
                active={activeFeaturedCategory === `popular`}
                onClick={() => setActiveFeaturedCategory(`popular`)}>
                <S.SectionHeader>Popular</S.SectionHeader>
                <S.SectionPickerIcon src={`/fc/icon-fire.png`} />
              </S.SectionPickerItem>
            </S.SectionPicker>
            <S.Section>
              {(activeFeaturedCategory === `new`
                ? newReports
                : popularReports
              ).map(r => renderReport(r, false))}
              {(activeFeaturedCategory === `new`
                ? popularReports
                : newReports
              ).map(r => renderReport(r, true))}
            </S.Section>
          </>
        ) : loadingReports ? (
          <LoadingContent />
        ) : null}
        <Desktop>
          <S.Section smallPadding ref={reportsHeaderRef}>
            <S.Search>
              <S.SearchIcon name="search" />
              <S.SearchInput
                placeholder="Search posts"
                value={searchQuery}
                onChange={
                  ((e, {value}) =>
                    setSearchQuery(value)) as StrictInputProps['onChange']
                }
              />
              <S.ClearSearchButton
                onClick={() => setSearchQueryLiveAndDebounced('')}>
                Clear Search
              </S.ClearSearchButton>
              <PopupDropdown
                options={LANGUAGE_OPTIONS}
                offset={`0px, -16px`}
                trigger={
                  <div>
                    <DropdownTrigger width={100} value={language} />
                  </div>
                }
              />
            </S.Search>
          </S.Section>
        </Desktop>
        {isBlogPage && (
          <Mobile>
            <S.MobileNav insideContainer>
              <S.MobileNavText>Popular Topics</S.MobileNavText>
              <S.MobileNavMenuTrigger
                onClick={() => setMobileNavMenuOpen(prev => !prev)}>
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenuTriggerBar />
                <S.MobileNavMenu
                  active={mobileNavMenuOpen}
                  onClick={e => e.stopPropagation()}>
                  <S.MobileNavSearch
                    placeholder="Search posts"
                    value={searchQuery}
                    onChange={
                      ((e, {value}) =>
                        setSearchQuery(value)) as StrictInputProps['onChange']
                    }
                  />
                  <S.MobileNavDropdown>
                    {POPULAR_TOPICS.map(topicLinkForm => (
                      <PopularTopic
                        key={topicLinkForm}
                        topicLinkForm={topicLinkForm}
                      />
                    ))}
                  </S.MobileNavDropdown>
                </S.MobileNavMenu>
              </S.MobileNavMenuTrigger>
            </S.MobileNav>
          </Mobile>
        )}
        {loadingReports ? (
          <LoadingContent />
        ) : (
          <>
            <S.Section>{reports.map(r => renderReport(r, false))}</S.Section>
            <Pagination />
          </>
        )}
      </S.LeftColumn>

      <S.RightColumn>
        {futureEvents.length > 0 && (
          <>
            <S.SectionHeader>Upcoming Events</S.SectionHeader>
            <S.Section extraPadding>
              {futureEvents.slice(0, 5).map(e => (
                <S.BlogPageEvent key={e.id} to={e.url}>
                  <S.BlogPageEventTime>
                    {new Date(e.startDate).toDateString()}
                  </S.BlogPageEventTime>
                  <S.BlogPageEventName>{e.name}</S.BlogPageEventName>
                  <S.BlogPageEventImage
                    src={e.fcMetadata?.imageURL ?? `/logo-transparent.png`}
                  />
                </S.BlogPageEvent>
              ))}
            </S.Section>
          </>
        )}
        {isBlogPage && (
          <>
            <S.SectionHeader>Popular Topics</S.SectionHeader>
            {POPULAR_TOPICS.map(topicLinkForm => (
              <PopularTopic key={topicLinkForm} topicLinkForm={topicLinkForm} />
            ))}
          </>
        )}
      </S.RightColumn>
    </S.Columns>
  );
});

type PopularTopicProps = {topicLinkForm: string};

const PopularTopic: React.FC<PopularTopicProps> = React.memo(
  ({topicLinkForm}) => {
    const {activeTag, tagsV2, reportIDSetByTagName} = useFCContext();

    const topic = tagsV2.find(t => t.linkForm === topicLinkForm);
    if (topic == null) {
      return null;
    }

    const topicTags = _.sortBy(
      tagsV2.filter(t => t.parent === topic.id && !t.hidden),
      t => -(reportIDSetByTagName.get(t.name)?.size ?? 0)
    );
    if (topicTags.length === 0) {
      return null;
    }

    return (
      <>
        <Desktop>
          <S.Section smallMargin>
            <S.BlogPageListHeader>{topic.name}</S.BlogPageListHeader>
            {topicTags.slice(0, 5).map(t => (
              <S.BlogPageListItem
                key={t.id}
                to={getSetActiveTagLink(t.linkForm)}
                onClick={() => {
                  trackClickTag(t, `FC right nav bar`);
                }}
                active={activeTag === t.name}>
                {t.name}
              </S.BlogPageListItem>
            ))}
            <S.BlogPageListItem to={getSetActiveTopicLink(topic)}>
              All
            </S.BlogPageListItem>
          </S.Section>
        </Desktop>
        <Mobile>
          <S.MobileNavDropdownTrigger>
            {topic.name}{' '}
            <S.MobileNavDropdownTriggerIcon name={`chevron-down`} />
          </S.MobileNavDropdownTrigger>
          {topicTags.slice(0, 5).map(t => (
            <S.MobileNavDropdownLink
              key={t.id}
              to={getSetActiveTagLink(t.linkForm)}
              onClick={() => {
                trackClickTag(t, `FC mobile tags nav menu`);
              }}>
              {t.name}
            </S.MobileNavDropdownLink>
          ))}
          <S.MobileNavDropdownLink to={getSetActiveTopicLink(topic)}>
            All
          </S.MobileNavDropdownLink>
        </Mobile>
      </>
    );

    function trackClickTag(t: TagV2, location: string): void {
      window.analytics?.track(`FC Tag Clicked`, {
        location,
        tagID: t.id,
        tagName: t.name,
      });
    }
  }
);

const EVENT_PAGE_SIZE = 9;

type EventsPageProps = {};

const EventsPage: React.FC<EventsPageProps> = React.memo(() => {
  const {
    loadingEvents,
    events,
    subscribedToNewsletter,
    loadingNewsletterSubscription,
  } = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
  const [region] = useState<string>('');
  const [topic] = useState<string>('');

  const [fcEvents, setFCEvents] = useState(events);
  useEffect(() => {
    if (!loadingEvents) {
      setFCEvents(events);
    }
  }, [loadingEvents, events]);

  const [pastEventsShown, setPastEventsShown] = useState(EVENT_PAGE_SIZE);
  const [futureEventsShown, setFutureEventsShown] = useState(EVENT_PAGE_SIZE);
  const [pastEvents, futureEvents] = useMemo(() => {
    return [
      // Events are already returned from `useBizzaboEvents` sorted
      // latest first, which is what we want for past events.
      fcEvents.filter(e => new Date(e.startDate).valueOf() <= Date.now()),

      // For future events, we want to sort them earliest first instead.
      _.sortBy(
        fcEvents.filter(e => new Date(e.startDate).valueOf() > Date.now()),
        e => {
          const d = new Date(e.startDate);
          return d.valueOf();
        }
      ),
    ];
  }, [fcEvents]);
  const showPastShowMoreButton = pastEventsShown < pastEvents.length;
  const showFutureShowMoreButton = futureEventsShown < futureEvents.length;

  // REGION
  const regionOptions = useMemo(() => {
    const allRegions = _.uniq(
      _.compact(events.flatMap(e => e.timezone.split('/')[0]))
    );
    const regionOpts = allRegions.map(r => ({
      text: r,
      value: r.toLowerCase(),
    }));
    const allOpts = [{text: '-- Region --', value: ''}, ...regionOpts];
    return allOpts;
  }, [events]);
  const regionValToText = useMemo(() => {
    const regionMap = new Map<string, string>();
    regionOptions.forEach(o => regionMap.set(o.value, o.text));
    return regionMap;
  }, [regionOptions]);

  // TOPIC
  const topicOptions = useMemo(() => {
    const allTopics = _.uniq(_.compact(events.flatMap(e => e.type)));
    const topicOpts = allTopics.map(t => ({
      text: t,
      value: t.toLowerCase().replace(/&/g, 'and').replace(/ /g, '-'),
    }));
    const allOpts = [{text: '-- Topic --', value: ''}, ...topicOpts];
    return allOpts;
  }, [events]);
  const topicValToText = useMemo(() => {
    const topicMap = new Map<string, string>();
    topicOptions.forEach(o => topicMap.set(o.value, o.text));
    return topicMap;
  }, [topicOptions]);

  // SEARCH
  const searchEvents = useCallback(() => {
    let results = events;
    // query search
    if (searchQuery !== '') {
      results = fuzzyMatchWithMapping(results, searchQuery, e => e.name);
    }
    // from date
    if (startDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart >= startDateFilter;
      });
    }
    // to date
    if (endDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart <= endDateFilter;
      });
    }
    // region of choice
    if (region !== '') {
      results = results.filter(e =>
        e.timezone.includes(regionValToText.get(region) ?? '')
      );
    }
    // topic of choice
    if (topic !== '') {
      results = results.filter(e =>
        e.type?.includes(topicValToText.get(topic) ?? '')
      );
    }
    setFCEvents(results);
    setPastEventsShown(EVENT_PAGE_SIZE);
    setFutureEventsShown(EVENT_PAGE_SIZE);
  }, [
    events,
    regionValToText,
    topicValToText,
    searchQuery,
    startDateFilter,
    endDateFilter,
    region,
    topic,
  ]);

  useEffect(() => {
    searchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, startDateFilter, endDateFilter]);

  return (
    <>
      <S.EventsContainer>
        <S.EventsControls>
          <S.EventsControlsInput
            placeholder={`Search events`}
            icon={<S.EventsControlsIcon name="search" />}
            value={searchQuery}
            onChange={
              ((e, {value}) =>
                setSearchQuery(value)) as StrictInputProps['onChange']
            }
          />
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              placeholder="Select from date"
              value={startDateFilter ?? undefined}
              onChange={d => {
                setStartDateFilter(d);
              }}
            />
          </S.EventsControlsDatetimeContainer>
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              placeholder="Select to date"
              value={endDateFilter ?? undefined}
              onChange={d => {
                setEndDateFilter(d);
              }}
            />
          </S.EventsControlsDatetimeContainer>
          {/* <S.EventsControlsDropdown
            selection
            placeholder={`-- Region --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={regionOptions}
            value={region}
            onChange={
              ((e, {value}) =>
                setRegion(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsControlsDropdown
            selection
            placeholder={`-- Topic --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={topicOptions}
            value={topic}
            onChange={
              ((e, {value}) =>
                setTopic(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsButton onClick={searchEvents}>Search</S.EventsButton> */}
        </S.EventsControls>
        {loadingEvents ? (
          <LoadingContent />
        ) : (
          <>
            {futureEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Upcoming events</S.EventsHeader>
                <S.EventsText>
                  Check out our upcoming webinars, paper reading groups,
                  tutorials, and more! We host multiple events a week and
                  they're always free for our community. Hope to see you there!
                </S.EventsText>
                <S.EventEntries>
                  {futureEvents
                    .slice(0, futureEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={imgSrcWithDefault(fcMetadata?.imageURL)}
                        />
                        <S.EventEntryDate>
                          {new Date(startDate).toDateString()}
                        </S.EventEntryDate>
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showFutureShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setFutureEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
            {pastEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Rewatch past events</S.EventsHeader>
                <S.EventsText>
                  All our previous events are free to watch. Check out anything
                  you've missed in the past couple months.
                </S.EventsText>
                <S.EventEntries>
                  {pastEvents
                    .slice(0, pastEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={imgSrcWithDefault(fcMetadata?.imageURL)}
                        />
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showPastShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setPastEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
          </>
        )}
      </S.EventsContainer>
      {!subscribedToNewsletter && !loadingNewsletterSubscription && (
        <S.EventsCTA>
          <S.EventsCTAText>
            Don't want to miss any upcoming W&B Events?
            <br />
            Subscribe to reminders!
          </S.EventsCTAText>
          <S.IntroButtonImg
            src={`/fc/button-subscribe.png`}
            onClick={() => setSubscribeModalOpen(true)}
          />
        </S.EventsCTA>
      )}
    </>
  );
});

type ReposPageProps = {};

const ReposPage: React.FC<ReposPageProps> = React.memo(() => {
  const {loading, reposWithGitHubMetadata: repos} = useFCContext();
  const [reposSearch, setReposSearch] = useState('');

  const filteredRepos = useMemo(() => {
    if (reposSearch === '') {
      return repos;
    }
    return fuzzyMatchWithMapping(repos, reposSearch, e =>
      [e.url, e.name, e.description].join(' ')
    );
  }, [reposSearch, repos]);
  return (
    <Container>
      <Header style={{textAlign: 'center'}} size="huge">
        Trending Projects
        <Header.Subheader>
          Browse popular machine learning projects
        </Header.Subheader>
      </Header>
      <div style={{paddingBottom: 10}}>
        <Input
          icon="search"
          value={reposSearch}
          placeholder="Search..."
          onChange={(e, {value}) => {
            setReposSearch(value);
          }}
        />
      </div>
      {loading ? <Loader name="repos-page" /> : <Repos rows={filteredRepos} />}
    </Container>
  );
});

type ReposProps = {
  rows: RepoWithGitHubMetadata[];
};

const Repos: React.FC<ReposProps> = React.memo(({rows}) => {
  return (
    <S.RepoList>
      {rows.map(repo => {
        const {id, name, description, stars, forks, imageURL} = repo;
        return (
          <S.RepoListItem
            key={id}
            to={getSetActiveRepoLink(repo)}
            onClick={() => {
              window.analytics?.track(`FC Project Clicked`, {
                location: `FC projects page`,
                projectID: id,
                projectName: name,
              });
            }}>
            <S.RepoListItemImage src={imageURL} />
            <S.RepoListItemContent>
              <S.RepoListItemHeaderRow>
                <S.RepoListItemHeader>{name}</S.RepoListItemHeader>
                <S.RepoListItemStats>
                  <S.RepoListItemStat>
                    <Icon name="star" />
                    {stars}
                  </S.RepoListItemStat>
                  <S.RepoListItemStat>
                    <Icon name="branch-commit" />
                    {forks}
                  </S.RepoListItemStat>
                  {/* <S.RepoListItemStat>
                  <Icon name="find" />
                  {watching}
                </S.RepoListItemStat>
                <S.RepoListItemStat>
                  <Icon name="fire" />
                  {0}
                </S.RepoListItemStat> */}
                </S.RepoListItemStats>
              </S.RepoListItemHeaderRow>
              <S.RepoListItemBody>{description}</S.RepoListItemBody>
            </S.RepoListItemContent>
          </S.RepoListItem>
        );
      })}
    </S.RepoList>
  );
});

type RepoGitHubMetadata = {
  stars: number;
  watching: number;
  forks: number;
};

type RepoWithGitHubMetadata = RepoWithUsage & RepoGitHubMetadata;

const RepoPage: React.FC = React.memo(() => {
  const {reposWithGitHubMetadata: repos, activeRepo} = useFCContext();

  const repo = useMemo(
    () => repos.find(r => r.linkForm === activeRepo),
    [repos, activeRepo]
  );

  const links = useMemo(
    () => repo?.links.filter(l => !l.pending) ?? [],
    [repo]
  );

  if (repo == null) {
    return null;
  }

  return (
    <PageWithSidebar
      sidebar={<RepoSidebar repo={repo} />}
      sidebarStyle={{minHeight: '100%'}}>
      <S.RepoContent>
        <S.RepoCustomContent>
          <Markdown content={repo.customContent} />
        </S.RepoCustomContent>
        {links.length > 0 && (
          <>
            <div className="header-wrapper">
              <ImageIconHeader icon="gallery" text="Links" />
            </div>
            <div className="report-showcase">
              <Card.Group itemsPerRow={2}>
                {links.map(({id, url, name, description, imageURL}) => {
                  return (
                    <Link
                      key={id}
                      to={url}
                      className={`ui card report-showcase-item`}>
                      <Card.Content className="report-showcase-item-content">
                        <img
                          src={imageURL}
                          className={`report-showcase-item-image`}
                          alt={`link thumbnail`}
                        />
                        <div className="report-showcase-item-text">
                          <Card.Header className="report-showcase-item-title">
                            {name}
                          </Card.Header>
                          <Card.Description className="report-showcase-item-description">
                            {description}
                          </Card.Description>
                        </div>
                      </Card.Content>
                    </Link>
                  );
                })}
              </Card.Group>
            </div>
          </>
        )}
      </S.RepoContent>
    </PageWithSidebar>
  );
});

type RepoSidebarProps = {repo: RepoWithGitHubMetadata};

const RepoSidebar: React.FC<RepoSidebarProps> = React.memo(
  ({repo: {name, description, imageURL, stars, watching, forks}}) => {
    return (
      <div className="profile-sidebar">
        <div className="inner-content">
          <S.RepoImage src={imageURL} />
          <S.RepoSidebarName>{name}</S.RepoSidebarName>
          <S.RepoDescription>{description}</S.RepoDescription>
          <S.RepoGitHubMetadata>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="star" /> {stars}
            </S.RepoGitHubMetadataRow>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="branch-commit" />
              {forks}
            </S.RepoGitHubMetadataRow>
          </S.RepoGitHubMetadata>
        </div>
      </div>
    );
  }
);

async function getGitHubMetadataForRepo({
  url,
}: RepoWithUsage): Promise<RepoGitHubMetadata> {
  const [owner, repoName] = url.split('/').slice(-2);
  // eslint-disable-next-line wandb/no-unprefixed-urls
  const resp = await fetch(`https://api.github.com/repos/${owner}/${repoName}`);
  const json = await resp.json();
  return {
    stars: json.stargazers_count,
    watching: json.watchers_count,
    forks: json.forks_count,
  };
}

type TopicPageProps = {};

const TopicPage: React.FC<TopicPageProps> = React.memo(() => {
  const {tagsV2, activeTopic, reportIDSetByTagName} = useFCContext();
  if (activeTopic == null) {
    return null;
  }

  const topic = tagsV2.find(t => t.linkForm === activeTopic);
  if (topic == null) {
    return null;
  }

  const topicChildren = _.sortBy(
    tagsV2.filter(t => t.parent === topic.id),
    t => -(reportIDSetByTagName.get(t.name)?.size ?? 0)
  );

  return (
    <>
      <S.TopicPageHeader>{topic.name}</S.TopicPageHeader>
      <S.TopicPageTags>
        {topicChildren.map(({id, name, linkForm, imageURL}) => (
          <S.TopicPageTag key={id} to={getSetActiveTagLink(linkForm)}>
            <S.TopicPageTagImage src={imgSrcWithDefault(imageURL)} />
            <S.TopicPageTagName>{name}</S.TopicPageTagName>
          </S.TopicPageTag>
        ))}
      </S.TopicPageTags>
    </>
  );
});

type SubscribeModalProps = {};

const SubscribeModal: React.FC<SubscribeModalProps> = React.memo(() => {
  const {subscribeModalOpen} = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();

  const viewer = useViewer();
  const loggedIn: boolean = useMemo(() => viewer != null, [viewer]);

  const [createNewsletterSubscription] =
    useCreateNewsletterSubscriptionMutation({
      context: propagateErrorsContext(),
    });

  const subscriptionInputRef = useRef<HTMLDivElement>(null);

  const [subscriptionInput, setSubscriptionInput] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const createSubscription = useCallback(async () => {
    try {
      if (!loggedIn && !isValidEmail(subscriptionInput)) {
        setSubscribeError('Please enter a valid email.');
        subscriptionInputRef.current?.focus();
        return;
      }

      const mutationVars = loggedIn ? {} : {email: subscriptionInput};
      await createNewsletterSubscription({variables: mutationVars});

      setSubscribeSuccess(true);
      setSubscriptionInput('');
      setSubscribeError('');
    } catch (err) {
      console.error(err);
      subscriptionInputRef.current?.focus();
      setSubscriptionInput('');
      setSubscribeError('Oops, something went wrong.');
    }
  }, [loggedIn, subscriptionInput, createNewsletterSubscription]);

  if (!subscribeModalOpen) {
    return null;
  }
  return (
    <S.SubscribeModal open onClose={() => setSubscribeModalOpen(false)}>
      <S.SubscribeSection>
        <S.SubscribeHeader>
          Get weekly updates with the latest ML news.
        </S.SubscribeHeader>
        {subscribeSuccess ? (
          <S.SubscribeSuccessMessage>
            <Icon name="checkmark" />
            Subscribed!
          </S.SubscribeSuccessMessage>
        ) : (
          <S.SubscribeInputContainer>
            <S.SubscribeInputMessageContainer loggedIn={loggedIn}>
              <S.SubscribeInput
                placeholder="Email Address"
                invalid={subscribeError !== ''}
                value={subscriptionInput}
                ref={subscriptionInputRef}
                onChange={
                  ((e, {value}) =>
                    setSubscriptionInput(value)) as StrictInputProps['onChange']
                }
              />
              <S.SubscribeInvalidMessage invalid={subscribeError !== ''}>
                {subscribeError}
              </S.SubscribeInvalidMessage>
            </S.SubscribeInputMessageContainer>
            <S.SubscribeButton
              content="Subscribe"
              onClick={createSubscription}
            />
          </S.SubscribeInputContainer>
        )}
      </S.SubscribeSection>
    </S.SubscribeModal>
  );
});

type ResponsiveProps = {desktop: ReactNode; mobile: ReactNode};

const Responsive: React.FC<ResponsiveProps> = React.memo(
  ({desktop, mobile}) => {
    const {width: screenWidth} = useWindowSize();
    const isDesktop = screenWidth > S.TABLET_WIDTH;
    return <>{isDesktop ? desktop : mobile}</>;
  }
);

type ResponsiveSpaceProps = {};

const ResponsiveSpace: React.FC<ResponsiveSpaceProps> = React.memo(() => {
  return <Responsive desktop={` `} mobile={<br />} />;
});

type DesktopProps = {};

const Desktop: React.FC<DesktopProps> = React.memo(({children}) => {
  return <Responsive desktop={children} mobile={null} />;
});

type MobileProps = {};

const Mobile: React.FC<MobileProps> = React.memo(({children}) => {
  return <Responsive desktop={null} mobile={children} />;
});

type FCReportsResult = {
  loadingReports: boolean;
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  reports: ReportMetadata[];
  updateReportMetadata: (
    id: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
};

function useFCReports({
  reportIDsWithTagV2IDs,
  tagsV2,
  activeTag,
  language,
  searchQueryDebounced,
  reportBelongsToTag,
}: Pick<
  FCContextState,
  | 'reportIDsWithTagV2IDs'
  | 'tagsV2'
  | 'activeTag'
  | 'language'
  | 'searchQueryDebounced'
  | 'reportBelongsToTag'
>): FCReportsResult {
  const {
    loading: reportMetadataLoading,
    reportMetadatas,
    updateReportMetadata,
  } = useReportMetadataWithCache({
    reportIDsWithTagV2IDs,
    tags: tagsV2,
    activeTag,
    fetchAll: searchQueryDebounced !== '',
    reportBelongsToTag,
  });

  const reports = useMemo(
    () => filterAndSortReports(reportMetadatas, language),
    [reportMetadatas, language]
  );

  const {newReports, popularReports, notFeaturedReports} = useMemo(
    () =>
      splitReportsByFeatured({
        reports,
        tags: tagsV2,
        activeTag,
        language,
        reportBelongsToTag,
      }),
    [reports, tagsV2, activeTag, language, reportBelongsToTag]
  );

  const searchedReports = useMemo(() => {
    if (searchQueryDebounced === '') {
      return notFeaturedReports;
    }
    return fuzzyMatchWithMapping(
      reports,
      searchQueryDebounced,
      (r: ReportMetadata) => r.displayName
    );
  }, [notFeaturedReports, reports, searchQueryDebounced]);

  return {
    loadingReports: reportMetadataLoading,
    newReports,
    popularReports,
    reports: searchedReports,
    updateReportMetadata,
  };
}

function filterAndSortReports(
  reports: ReportMetadata[],
  language: Language
): ReportMetadata[] {
  return _.sortBy(
    reports
      .filter(r => !r.pending)
      .filter(reportIsPastPublishDate)
      .filter(r => r.language === language),
    r => {
      const addedAtTS = new Date(r.addedAt ?? '').valueOf();
      return -addedAtTS;
    }
  );
}

function reportIsPastPublishDate(r: ReportMetadata): boolean {
  if (r.addedAt == null) {
    return false;
  }
  const addedAt = new Date(r.addedAt);
  const now = new Date();
  return now >= addedAt;
}

type ReportsSplitByFeatured = {
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  notFeaturedReports: ReportMetadata[];
};

type SplitReportsByFeaturedParams = {
  reports: ReportMetadata[];
  tags: TagV2[];
  activeTag: string;
  language: Language;
  reportBelongsToTag: (reportID: string, tagName: string) => boolean;
};

function splitReportsByFeatured({
  reports,
  tags,
  activeTag,
  language,
  reportBelongsToTag,
}: SplitReportsByFeaturedParams): ReportsSplitByFeatured {
  const tag = tags.find(t => t.name === activeTag);
  if (tag == null) {
    return {newReports: [], popularReports: [], notFeaturedReports: []};
  }

  // const announcementReportIDSet = new Set(
  //   reports.filter(r => r.announcement).map(r => r.id)
  // );
  // const categoryNameSet = new Set(categories.map(t => t.name));
  // const tagIsCategory = (tagName: string) => categoryNameSet.has(tagName);
  // const announcementAndFeaturedIDs = _.uniq([
  //   // only show announcements when active tag is a category
  //   ...(tagIsCategory(activeTag) ? announcementReportIDSet : []),
  //   ...tag.featuredReportIDs,
  // ]);
  // const announcementAndFeaturedIDSet = new Set(announcementAndFeaturedIDs);

  const reportsByID = new Map<string, ReportMetadata>(
    reports.map(r => [r.id, r])
  );
  const dontIncludeInPrimaryListIDSet = new Set(
    // We include popular reports in the primary list because otherwise
    // they may be missed if users never click on `Popular`
    tag.featuredReports
      .filter(r => r.category === `new`)
      .map(r => r.reportID)
  );
  const tagReports = reports.filter(r => reportBelongsToTag(r.id, activeTag));

  return {
    newReports: tag.featuredReports
      .filter(r => r.category === `new`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    popularReports: tag.featuredReports
      .filter(r => r.category === `popular`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    notFeaturedReports: tagReports.filter(
      ({id}) => !dontIncludeInPrimaryListIDSet.has(id)
    ),
  };
}

type PaginationResult<T> = {
  currentItems: T[];
  minPage: number;
  maxPage: number;
};

type PaginationParams<T> = {
  loadingItems: boolean;
  allItems: T[];
  itemsPerPage: number;
  currentPage: number;
  setCurrentPage: (n: number) => void;
};

function usePagination<T>({
  loadingItems,
  allItems,
  itemsPerPage,
  currentPage,
  setCurrentPage,
}: PaginationParams<T>): PaginationResult<T> {
  const total = allItems.length;
  const minPage = 1;
  const maxPage = Math.max(Math.ceil(total / itemsPerPage), 1);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allItems.slice(startIndex, startIndex + itemsPerPage);
  }, [allItems, currentPage, itemsPerPage]);

  // Clamp to last page if out of bounds
  useLayoutEffect(() => {
    if (!loadingItems && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingItems, currentPage, maxPage]);

  return {
    currentItems,
    minPage,
    maxPage,
  };
}

type PaginationProps = {};

const Pagination: React.FC<PaginationProps> = React.memo(() => {
  const location = useLocation();
  const {currentPage, minPage, maxPage, getSetCurrentPageLink} = useFCContext();

  const pagesWithEllipses: Array<number | 'ellipsis'> = useMemo(() => {
    if (maxPage - minPage < 5) {
      return _.range(minPage, maxPage + 1);
    }
    if (currentPage <= minPage + 2) {
      return [minPage, minPage + 1, minPage + 2, 'ellipsis', maxPage];
    }
    if (currentPage >= maxPage - 2) {
      return [minPage, 'ellipsis', maxPage - 2, maxPage - 1, maxPage];
    }
    return [minPage, 'ellipsis', currentPage, 'ellipsis', maxPage];
  }, [currentPage, minPage, maxPage]);

  const nextPageLink =
    currentPage < maxPage ? getSetCurrentPageLink(currentPage + 1) : undefined;
  const prevPageLink =
    currentPage > minPage ? getSetCurrentPageLink(currentPage - 1) : undefined;

  const renderPage = (p: number | 'ellipsis', i: number) => {
    if (p === 'ellipsis') {
      return (
        <S.PaginationPageDisabled key={i} disabled>
          <S.PaginationEllipsis>...</S.PaginationEllipsis>
        </S.PaginationPageDisabled>
      );
    }
    return (
      <S.PaginationPage
        key={i}
        to={getSetCurrentPageLink(p)}
        active={currentPage === p}>
        {p}
      </S.PaginationPage>
    );
  };

  return (
    <S.PaginationContainer>
      <S.Pagination>
        <S.PaginationEdge
          to={prevPageLink ?? location}
          disabled={prevPageLink == null}>
          <S.PaginationIcon name={`chevron-back`} />
          <Desktop>Previous</Desktop>
        </S.PaginationEdge>
        <S.PaginationPages>
          {pagesWithEllipses.map(renderPage)}
        </S.PaginationPages>
        <S.PaginationEdge
          to={nextPageLink ?? location}
          disabled={nextPageLink == null}>
          <Desktop>Next</Desktop>
          <S.PaginationIcon name={`chevron-next`} />
        </S.PaginationEdge>
      </S.Pagination>
    </S.PaginationContainer>
  );
});

type LoadingContentProps = {};

const LoadingContent: React.FC<LoadingContentProps> = React.memo(() => {
  return (
    <S.LoaderContainer>
      <S.Loader name="loading-content" />
    </S.LoaderContainer>
  );
});

type StateFromURL = {
  page: FCPage;
  activeTag: string;
  activeReportID: string | null;
  activeRepo: string | null;
  activeTopic: string | null;
  language: Language;
  currentPage: number;
};

type DeriveStateFromURLParams = {
  params: RouteParams;
  reportIDsWithTagV2IDs: ReportIDWithTagIDs[];
  tags: TagV2[];
  homeCategoryName: string;
};

function useDeriveStateFromURL({
  params,
  reportIDsWithTagV2IDs,
  tags,
  homeCategoryName,
}: DeriveStateFromURLParams): StateFromURL {
  const language = useDeriveLanguage();
  const currentPage = useDeriveCurrentPage();
  if (isReportPageParams(params)) {
    const activeReportID =
      parseReportParams(params.reportNameAndID).reportId ?? null;
    const tagPage = deriveTagPage({
      reportIDsWithTagV2IDs,
      tags,
      reportID: activeReportID,
    });
    return {
      page: tagPage ?? `blog`,
      activeTag: ``,
      activeReportID,
      activeRepo: null,
      activeTopic: null,
      language,
      currentPage,
    };
  }

  if (isRepoParams(params)) {
    return {
      page: `projects`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: params.repoName,
      activeTopic: null,
      language,
      currentPage,
    };
  }

  if (isTopicParams(params)) {
    return {
      page: `topics`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: null,
      activeTopic: params.topic,
      language,
      currentPage,
    };
  }

  const {page, tag} = params;
  return {
    page: derivePage({pageParam: page}),
    activeTag: deriveActiveTag({
      pageParam: page,
      tagParam: tag,
      tags,
      homeCategoryName,
    }),
    activeReportID: null,
    activeRepo: null,
    activeTopic: null,
    language,
    currentPage,
  };
}

type DerivePageParams = {pageParam: string | undefined};

function derivePage({pageParam}: DerivePageParams): FCPage {
  if (pageParam != null && isFCPage(pageParam)) {
    return pageParam;
  }
  return MAIN_PAGE;
}

type DeriveActiveTagParams = {
  pageParam: string | undefined;
  tagParam: string | undefined;
  tags: TagV2[];
  homeCategoryName: string;
};

function deriveActiveTag({
  pageParam,
  tagParam,
  tags,
  homeCategoryName,
}: DeriveActiveTagParams): string {
  const effectiveTagParam = (TAG_PAGES as string[]).includes(pageParam ?? '')
    ? pageParam
    : tagParam;
  if (effectiveTagParam == null) {
    return homeCategoryName;
  }
  return formatGalleryTag(tags, effectiveTagParam);
}

function useDeriveLanguage(): Language {
  const location = useLocation();
  const {language} = qs.parse(location.search);
  if (
    language != null &&
    typeof language === 'string' &&
    isLanguage(language)
  ) {
    return language;
  }
  return DEFAULT_LANGUAGE;
}

function useDeriveCurrentPage(): number {
  const location = useLocation();
  const {page} = qs.parse(location.search);
  if (page != null && typeof page === 'string') {
    return Number(page);
  }
  return 1;
}

function useActiveReportParams(reportID: string | null): {
  loading: boolean;
  reportParams: ReportParams | null;
} {
  const {loading, reportMetadatas} = useReportMetadata(
    reportID != null ? [reportID] : []
  );

  const reportParams = useMemo(() => {
    if (reportMetadatas == null || reportMetadatas.length === 0) {
      return null;
    }
    const {id, entityName, project, displayName} = reportMetadatas[0];
    return {
      id,
      entityName: project?.entityName || entityName,
      projectName: project?.name,
      displayName,
    };
  }, [reportMetadatas]);

  return {loading, reportParams};
}

function useGitHubMetadata(repos: RepoWithUsage[]): RepoWithGitHubMetadata[] {
  const [reposWithGitHubMetadata, setReposWithGitHubMetadata] = useState<
    RepoWithGitHubMetadata[]
  >([]);

  useEffect(() => {
    (async () => {
      const metadatas = await Promise.all(repos.map(getGitHubMetadataForRepo));
      setReposWithGitHubMetadata(
        repos.map((r, i) => ({...r, ...metadatas[i]}))
      );
    })();
  }, [repos]);

  return reposWithGitHubMetadata;
}
